import { Input, Output, EventEmitter, Component, OnInit } from "@angular/core";
import DataSource from "devextreme/data/data_source";
import { Product } from "../../../Model/Catalog/Product";
import { Catalog } from "../../../Model/Catalog/Catalog";
import { WawiIntegration } from "../../../Model/Catalog/WawiIntegration";
import { WawiList } from "../../../Model/wawi/WawiList";
import { UserManagementService } from "../../../Services/userManagment.service";
import { ValidationService } from "../../../Services/Validation/validation.service";
import { WawiListService } from "../../../Services/wawiList.service";
import { LoginService } from "../../../Services/login.service";
import { CatalogService } from "../../../Services/CatalogManagement/catalog.service";
import { ProductService } from "../../../Services/CatalogManagement/product.service";

@Component({
  selector: "wawi",
  templateUrl: "wawi.component.html",
  styleUrls: ["wawi.component.css", "../template.css"],
  standalone: false
})
export class WawiComponent implements OnInit {
  @Input() model: Product = new Product();
  @Input() showLinkButtons: boolean = false;
  @Input() templateView: boolean = false;

  private _selectedTabToShow = "Wawi";

  @Input()
  set selectedTabToShow(value: string) {
    this._selectedTabToShow = value ?? "Wawi";
    this.updateFilteredFields();
  }

  get selectedTabToShow(): string {
    return this._selectedTabToShow;
  }

  @Output() onValueChanged = new EventEmitter<any>();

  @Input() selectedElements: string[] = [""];
  @Output() elementSelected = new EventEmitter<string>();

  @Input() showDragHandle: boolean = false;
  @Input() dragFilter: string = "noDrag";

  @Output() excelElementDropped = new EventEmitter<any>();

  wawiLists: WawiList[] = [];
  wawiListDataSource: DataSource;
  wawiListDataSourceMap = new Map<string, DataSource>();
  filteredCustomFields: any[] = [];

  constructor(
    public loginService: LoginService,
    public userManagement: UserManagementService,
    public wawiListService: WawiListService,
    public validationService: ValidationService,
    public catalogService: CatalogService,
    public productService: ProductService
  ) {
    this.createLines = this.createLines.bind(this);
  }

  ngOnInit(): void {
    if (!this.model.wawi) {
      this.model.wawi = new WawiIntegration();
    }

    this.wawiListService
      .getAllLists(this.loginService.currentUser.customerId)
      .subscribe(result => {
        this.wawiLists = result;
        this.wawiListDataSource = new DataSource({
          store: result,
          paginate: true,
          pageSize: 5
        });

        this.updateFilteredFields();
      });
  }

  private updateFilteredFields(): void {
    if (!this.model.wawi) {
      this.model.wawi = new WawiIntegration();
    }

    if (!this.model?.wawi?.customFields) {
      this.filteredCustomFields = [];
      return;
    }

    const currentFields = [];

    const isCustomTab = this.selectedTabToShow?.startsWith("tab:");
    const customTabId = isCustomTab ? Number(this.selectedTabToShow.substring(4)) : null;

    this.loginService.wawiSettings.customFields
      .filter(x => isCustomTab
        ? x.customTabId === customTabId
        : (!x.customTabId && x.tabOption === this.selectedTabToShow))
      .forEach(field => {
        const customWawiField = this.model.wawi.customFields.find(
          customField => customField.order === field.order
        );

        if (customWawiField) {
          if (field.fieldType === "List") {
            const list = this.wawiLists.find(wawiList => wawiList.id === field.listId);
            if (list) {
              const dataSource = new DataSource({
                store: list.fields,
                paginate: true,
                pageSize: 5
              });

              this.wawiListDataSourceMap.set(field.listId, dataSource);
            }

            customWawiField.listId = field.listId;
          }

          currentFields.push(customWawiField);
        }
      });

    this.filteredCustomFields = currentFields;
  }

  isVirtualCatalog(): boolean {
    return this.catalogService?.catalog?.isVirtual;
  }

  update(event = null, field?: string, system: string = "-1", element: string = "-1") {
    if (event) {
      event.dField = field;
      event.dSystem = system;
      event.dElement = element;
      event.createTemplateItem = true;
    }

    this.onValueChanged.emit(event);
  }

  showPlaceLabel(order: number): boolean {
    return false;
  }

  getLabel(order: number): string {
    const field = this.loginService.wawiSettings.customFields.find(x => x.order === order);
    return field ? field.name : "";
  }

  getFieldType(order: number): string {
    const field = this.loginService.wawiSettings.customFields.find(x => x.order === order);
    return field ? field.fieldType : "";
  }

  createLines() {
    this.productService.createLines(this.model).subscribe((result: Product) => {
      this.model = this.catalogService.setProduct(result);
      this.updateFilteredFields();
    });
  }

  hasLines(): boolean {
    if (this.selectedTabToShow?.startsWith("tab:")) {
      return false;
    }
    return (
      (this.loginService.wawiSettings.hasLine1 &&
        this.loginService.wawiSettings.line1TabOption === this.selectedTabToShow) ||
      (this.loginService.wawiSettings.hasLine2 &&
        this.loginService.wawiSettings.line2TabOption === this.selectedTabToShow) ||
      (this.loginService.wawiSettings.hasLine3 &&
        this.loginService.wawiSettings.line3TabOption === this.selectedTabToShow) ||
      (this.loginService.wawiSettings.hasLine4 &&
        this.loginService.wawiSettings.line4TabOption === this.selectedTabToShow)
    );
  }

  public elementSelectFunction(identifier: string, order: string = "-1") {
    if (order) {
      this.elementSelected.emit(`${identifier}_${order}`);
    } else {
      this.elementSelected.emit(`${identifier}`);
    }
  }

  public excelElementDrop(e: any) {
    this.excelElementDropped.emit(e);
  }

  public readOnlyForLinkedFields(field: string, system: string = "-1", element: string = "-1"): boolean {
    if (field != null) {
      const isLinked = this.model.isFieldLinked(field, system, element);
      if (isLinked && this.model.isChild != null && this.model.isChild) {
        return true;
      }
    }

    return false;
  }

  public showClearButton(field: string, system: string = "-1"): boolean {
    return this.readOnlyForLinkedFields(field, system) && !this.templateView;
  }

  public hasMasterOrChildOrIsTranslated(): boolean {
    return this.model.hasMasterOrChild();
  }

  public isFieldLinked(field: string, system: string = "-1", element: string = "-1"): boolean {
    return this.model.isFieldLinked(field, system, element);
  }

  canView(name: string): boolean {
    return !this.isVirtualCatalog() && this.userManagement.canView(name);
  }

  readOnly(name: string, catalog: Catalog = this.catalogService.catalog): boolean {
    return this.isVirtualCatalog() || !this.userManagement.canEdit(name, catalog);
  }

  getListElements(name: any) {
    const defaultDataSource = new DataSource({
      store: []
    });

    const customField = this.loginService.wawiSettings.customFields.find(x => x.order === name.order);
    if (!customField) {
      return defaultDataSource;
    }

    const result = this.wawiListDataSourceMap.get(customField.listId);
    return result ?? defaultDataSource;
  }
}